
/// <reference path="InvoiceFileCell.ts" />

class InvoiceFilesViewController extends MUIViewController
{
    static newInstance(): InvoiceFilesViewController {
        let vc = new InvoiceFilesViewController("invoice-files-view");
        vc.initWithResource("layout/invoices/InvoiceFilesView.html");
        return vc;
    }

    get preferredContentSize() {
        return new MIOSize(1024, 800);
    }

    private closeButton:MUIButton = null;
    private openButton:MUIButton = null;
    private tableView:UITableView = null;
    private previewPanel:MUIView = null;
    private noPreviewLabel:MUILabel = null;

    private _selectedIndex:number = -1;

    viewDidLoad(){
        super.viewDidLoad();

        this.closeButton = MUIOutlet(this, "close-btn", "MUIButton");
        this.closeButton.setAction(this, function(this:InvoiceFilesViewController){
            this.dismissViewController(true);
        });

        // The same file in a browser tab, to compare with the lines while reviewing.
        this.openButton = MUIOutlet(this, "open-btn", "MUIButton");
        this.openButton.setAction(this, function(this:InvoiceFilesViewController){
            let file = this._selectedIndex >= 0 && this._selectedIndex < this._files.length ? this._files[this._selectedIndex] : (this._files.length > 0 ? this._files[0] : null);
            if (file != null && file.url != null) window.open(file.url, "_blank", "noopener");
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.previewPanel  = MUIOutlet(this, "preview-panel",  "MUIView");
        this.noPreviewLabel = MUIOutlet(this, "no-preview-lbl", "MUILabel");
    }

    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);
        this.loadFiles();
    }

    private _document:DBCoreDocument = null;    
    set document(value:DBCoreDocument){
        this._document = value;
    }

    private _files:FileAttachment[] = [];

    private loadFiles(){
        if (this._document == null) return;
        let sds = [MIOSortDescriptor.sortDescriptorWithKey("createdAt", false)];
        let pf = "deletedAt = null && entityID = " + this._document.identifier;
        DBHelper.queryObjectsWithCompletion("FileAttachment", sds, MIOPredicate.predicateWithFormat(pf), [], this, function(this:InvoiceFilesViewController, files:FileAttachment[]){
            this._files = files;
            this.tableView.reloadData();
        });
    }

    // UITableViewDataSource

    numberOfSections(tableView:UITableView){ return 1; }

    numberOfRowsInSection(tableView:UITableView, section:number){
        return this._files.length;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let cell = tableView.dequeueReusableCellWithIdentifier("InvoiceFileCell") as InvoiceFileCell;
        cell.item = this._files[indexPath.row];
        cell.selected = (indexPath.row === this._selectedIndex);
        return cell;
    }

    // UITableViewDelegate

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        this._selectedIndex = indexPath.row;
        this.tableView.reloadData();
        let fileInfo = this._files[indexPath.row];
        this.showPreview(fileInfo);
    }

    private showPreview(fileInfo:FileAttachment){
        let url:string = fileInfo.url;
        if (url == null) return;

        // Clear existing preview content
        let panelLayer = this.previewPanel.layer as HTMLElement;
        while (panelLayer.firstChild) panelLayer.removeChild(panelLayer.firstChild);
        if (this.noPreviewLabel != null) this.noPreviewLabel.hidden = true;

        // Derive extension from name, falling back to the URL path
        let nameSource = fileInfo.name ?? url;
        let ext = nameSource.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
        let isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg"].indexOf(ext) >= 0;
        let isPDF   = ext === "pdf";

        if (!isPDF && !isImage) {
            window.open(url, "_blank");
            return;
        }

        // The file URL is a direct S3 URL — it does not need app-level auth headers.
        // Sending Authorization/DL-PLACE-ID to S3 triggers a CORS preflight that S3 rejects.
        // A plain fetch() avoids the preflight; the browser sends a simple cross-origin GET
        // which S3 accepts (CORS is configured on the S3 bucket for the production origin).
        fetch(url)
            .then(response => {
                if (!response.ok) throw new Error("HTTP " + response.status);
                return response.blob();
            })
            .then(rawBlob => {
                // Re-wrap with an explicit MIME type so the browser renders inline
                // instead of treating it as application/octet-stream.
                let mime      = isPDF ? "application/pdf"
                                      : "image/" + (ext === "jpg" ? "jpeg" : ext);
                let typedBlob = new Blob([rawBlob], { type: mime });
                let objectURL = URL.createObjectURL(typedBlob);

                if (isImage) {
                    let img = document.createElement("img");
                    img.src = objectURL;
                    img.style.maxWidth     = "100%";
                    img.style.maxHeight    = "100%";
                    img.style.objectFit    = "contain";
                    img.style.borderRadius = "6px";
                    img.style.margin       = "auto";
                    img.style.display      = "block";
                    panelLayer.appendChild(img);
                } else {
                    // Use <embed> instead of <iframe> — Chrome 120+ no longer renders
                    // PDFs inline inside iframes, even for blob: URLs.
                    let embed = document.createElement("embed");
                    embed.src              = objectURL;
                    embed.type             = "application/pdf";
                    embed.style.width      = "100%";
                    embed.style.height     = "100%";
                    embed.style.borderRadius = "6px";
                    panelLayer.appendChild(embed);
                }
            })
            .catch(() => {
                // fetch failed (likely CORS on localhost — run Chrome with
                // --disable-web-security to test locally).
                // Show a link instead of triggering a download via window.open.
                let msg = document.createElement("div");
                msg.style.margin     = "auto";
                msg.style.textAlign  = "center";
                msg.style.opacity    = "0.6";
                msg.style.padding    = "24px";
                msg.innerHTML = "Preview unavailable. <br><br>" +
                    "<a href='" + url + "' target='_blank' rel='noopener noreferrer'" +
                    " style='color:#4D90FE'>Open file in new tab</a>";
                panelLayer.appendChild(msg);
            });
    }
}

